import { create } from 'zustand'
import { Map } from 'ol'
import debounce from 'lodash.debounce'
import { transformExtent } from 'ol/proj'
import { ApriorMapVersionId } from 'utils/types'

interface MapStore {
  map?: Map
  zoom: number
  extent: number[]
  isSatellite: boolean
  missionExtent: number[] | null
  apriorMapVisible: boolean
  apriorMapOpacity: number
  tempApriorMapVersionId: ApriorMapVersionId | null | undefined
  setMap: (map?: Map) => void
  setSatelliteBaseMap: (val: boolean) => void
  setMissionExtent: (extent: number[] | null) => void
  toggleApriorMap: () => void
  setApriorMapOpacity: (opacity: number) => void
  resetApriorMapDisplay: () => void
  setTempApriorMapVersionId: (id: ApriorMapVersionId | null) => void
  clearTempApriorMapVersionId: () => void
}

let moveEndListener: ReturnType<typeof debounce> | undefined

const useMapStore = create<MapStore>((set, get) => ({
  isSatellite: false,
  apriorMapVisible: true,
  apriorMapOpacity: 1.0,
  tempApriorMapVersionId: undefined,
  missionExtent: null,
  extent: [],
  zoom: 0,
  setMap: map => {
    const previousMap = get().map
    if (previousMap && moveEndListener) {
      previousMap.un('moveend', moveEndListener)
      moveEndListener.cancel()
    }

    moveEndListener = undefined
    if (map) {
      const currentMap = map
      moveEndListener = debounce(() => {
        const zoom = currentMap.getView().getZoom()
        const extent = currentMap.getView().calculateExtent()
        const lonLatExtent = transformExtent(extent, 'EPSG:3857', 'EPSG:4326')
        set({ zoom, extent: lonLatExtent })
      }, 250)
      currentMap.on('moveend', moveEndListener)
    }
    set(() => ({ map }))
  },
  setSatelliteBaseMap: val => set(() => ({ isSatellite: val })),
  setMissionExtent: extent => set(() => ({ missionExtent: extent })),
  toggleApriorMap: () => set(state => ({ apriorMapVisible: !state.apriorMapVisible })),
  setApriorMapOpacity: opacity => set(() => ({ apriorMapOpacity: opacity })),
  resetApriorMapDisplay: () => set(() => ({ apriorMapVisible: true, apriorMapOpacity: 1.0 })),
  setTempApriorMapVersionId: id => set(() => ({ tempApriorMapVersionId: id })),
  clearTempApriorMapVersionId: () => set(() => ({ tempApriorMapVersionId: undefined })),
}))

export default useMapStore
